import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { baseUrl } from "../../../constants/api";
import { RootState } from "../../../store";

export default function AddNewQuestion() {
    const { folder, subfolder, questionId } = useParams<{ folder: string; subfolder: string; questionId?: string }>();
    const navigate = useNavigate();
    const token = useSelector((state: RootState) => state.account.token);

    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState<string[]>(["", "", "", ""]);
    const [correctOptionIndex, setCorrectOptionIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [fetchingQuestion, setFetchingQuestion] = useState(false);

    const isEditMode = Boolean(questionId);

    useEffect(() => {
        if (isEditMode && questionId && token) {
            const fetchQuestion = async () => {
                try {
                    setFetchingQuestion(true);
                    const response = await fetch(`${baseUrl}/api/questions/get-question/${questionId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        const q = data.question;
                        setQuestion(q.question);
                        // Ensure we always have 4 option slots
                        const padded = [...q.options];
                        while (padded.length < 4) padded.push("");
                        setOptions(padded);
                        setCorrectOptionIndex(q.correct_option_id);
                    } else {
                        console.error("Failed to fetch question");
                    }
                } catch (err) {
                    console.error("Error fetching question:", err);
                } finally {
                    setFetchingQuestion(false);
                }
            };
            fetchQuestion();
        }
    }, [questionId, token, isEditMode]);

    const handleOptionChange = (index: number, value: string) => {
        const updatedOptions = [...options];
        updatedOptions[index] = value;
        setOptions(updatedOptions);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const filteredOptions = options.filter((opt) => opt.trim() !== "");

        if (filteredOptions.length < 2) {
            alert("Please provide at least two valid options.");
            return;
        }

        if (correctOptionIndex === null || correctOptionIndex >= filteredOptions.length) {
            alert("Please select a valid correct option.");
            return;
        }

        const payload = {
            question,
            options: filteredOptions,
            correct_option_id: correctOptionIndex,
            tags: [folder?.toLowerCase() || ""],
            difficulty: "medium",
            topic_name: folder,
            subtopic_name: subfolder,
        };

        try {
            setLoading(true);
            let response;
            if (isEditMode && questionId) {
                response = await fetch(`${baseUrl}/api/questions/update-ques/${questionId}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });
            } else {
                response = await fetch(`${baseUrl}/api/questions/add-question`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ questions: [payload] }),
                });
            }

            if (response.ok) {
                alert(isEditMode ? "Question updated successfully!" : "Question added successfully!");
                navigate(-1);
            } else {
                alert("Failed to save question. Please try again.");
            }
        } catch (error) {
            console.error("Error saving question:", error);
            alert("An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-question-page">
            {/* Context banner — always visible */}
            <div className="question-context">
                <div className="context-badge">
                    <span className="context-label">Topic</span>
                    <span className="context-value">{folder}</span>
                </div>
                <div className="context-separator" />
                <div className="context-badge">
                    <span className="context-label">Subtopic</span>
                    <span className="context-value">{subfolder}</span>
                </div>
            </div>

            <div className="add-new-question-container">
                <h2>{isEditMode ? "Edit Question" : "Add New Question"}</h2>

                {fetchingQuestion ? (
                    <div className="loading-skeleton">
                        <div className="skeleton-line" style={{ height: "110px" }} />
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="skeleton-line" style={{ height: "52px" }} />
                            ))}
                        </div>
                        <div className="skeleton-line" style={{ height: "44px", width: "160px", alignSelf: "flex-end" }} />
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {/* Question textarea */}
                        <div>
                            <span className="question-field-label">Question</span>
                            <textarea
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                required
                                className="question-input"
                                placeholder="Type your question here…"
                            />
                        </div>

                        {/* Options */}
                        <div>
                            <span className="options-section-label">
                                Options &nbsp;
                                <span style={{ fontWeight: 400, color: "#888", textTransform: "none", letterSpacing: 0 }}>
                                    — select the radio button next to the correct answer
                                </span>
                            </span>
                            <div className="options-container">
                                {options.map((option, index) => (
                                    <div
                                        key={index}
                                        className={`option-item ${correctOptionIndex === index ? "selected-option" : ""}`}
                                        onClick={() => setCorrectOptionIndex(index)}
                                    >
                                        <input
                                            type="radio"
                                            id={`correct-option-${index}`}
                                            name="correctOption"
                                            checked={correctOptionIndex === index}
                                            onChange={() => setCorrectOptionIndex(index)}
                                            className="correct-option-radio"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className="option-letter-badge">
                                            {String.fromCharCode(65 + index)}
                                        </div>
                                        <div className="option-label">
                                            <input
                                                type="text"
                                                value={option}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    handleOptionChange(index, e.target.value);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                required
                                                placeholder={`Option ${String.fromCharCode(65 + index)}`}
                                                className="option-input"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {correctOptionIndex !== null && (
                                <p className="correct-hint">
                                    ✓ Option {String.fromCharCode(65 + correctOptionIndex)} marked as correct
                                </p>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="form-actions">
                            <button
                                type="button"
                                className="cancel-button"
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="submit-button" disabled={loading}>
                                {loading ? "Saving…" : isEditMode ? "Update Question" : "Save Question"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
